import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ALLERGEN_LABELS,
  ALLERGENS,
  APPLIANCE_LABELS,
  APPLIANCES,
  type Allergen,
  type Appliance,
  type MealPlaceDto,
  type PlaceKind,
} from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { EmptyState } from '../../components/EmptyState';
import { Field } from '../../components/Field';
import { HomeIcon } from '../../components/NavIcons';
import { SelectField } from '../../components/SelectField';
import { Section } from '../../components/Section';
import { SettingsNav } from '../../components/SettingsNav';
import { ChipToggles, MiniChips } from '../../components/profiles/ChipToggles';
import { api, queryKeys } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { messageForError } from '../../lib/errors';
import { scheduleKeys } from '../../lib/schedule';
import {
  NOTES_MAX,
  PLACE_KIND_LABEL,
  placeKindOptions,
  profileKeys,
  sortPlaces,
} from '../../lib/profiles';

interface PlaceDraft {
  id: string | null;
  name: string;
  kind: PlaceKind;
  appliances: Appliance[];
  bannedAllergens: Allergen[];
  notes: string;
}

const NEW_PLACE: PlaceDraft = {
  id: null,
  name: '',
  kind: 'work',
  appliances: [],
  bannedAllergens: [],
  notes: '',
};

const draftOf = (p: MealPlaceDto): PlaceDraft => ({
  id: p.id,
  name: p.name,
  kind: p.kind,
  appliances: [...p.appliances],
  bannedAllergens: [...p.bannedAllergens],
  notes: p.notes ?? '',
});

/**
 * Settings → Meal places (D32): home, work, school … Where a meal is eaten
 * decides what a packed lunch may need (appliances) and must avoid (banned
 * allergens).
 */
export function MealPlacesPage() {
  const qc = useQueryClient();
  const { auth } = useAuth();
  const canManage = auth.status === 'authed' && (auth.role === 'owner' || auth.role === 'adult');
  const places = useQuery({
    queryKey: profileKeys.places,
    queryFn: () => api.households.mealPlaces.list(),
  });
  const [editing, setEditing] = useState<PlaceDraft | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<MealPlaceDto | null>(null);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: profileKeys.places });
    // Deleting a place sends its meal times and plans home.
    void qc.invalidateQueries({ queryKey: scheduleKeys.mealTimes });
    void qc.invalidateQueries({ queryKey: queryKeys.plans.all });
  };

  const save = useMutation({
    mutationFn: (d: PlaceDraft) => {
      const body = {
        name: d.name.trim(),
        appliances: d.appliances,
        bannedAllergens: d.bannedAllergens,
        notes: d.notes.trim() === '' ? null : d.notes.trim(),
      };
      return d.id
        ? api.households.mealPlaces.update(
            d.id,
            d.kind === 'home' ? body : { ...body, kind: d.kind },
          )
        : api.households.mealPlaces.create({ ...body, kind: d.kind });
    },
    onSuccess: async () => {
      setEditing(null);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.households.mealPlaces.remove(id),
    onSuccess: async () => {
      setRemoving(null);
      await refresh();
    },
  });

  function open(d: PlaceDraft) {
    save.reset();
    setNameError(null);
    setEditing(d);
  }

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!editing) return;
    const name = editing.name.trim();
    const err =
      name === '' ? 'Enter a name.' : name.length > 80 ? 'Keep it under 80 characters.' : null;
    setNameError(err);
    if (!err) save.mutate(editing);
  }

  const list = sortPlaces(places.data?.places ?? []);

  return (
    <AppShell title="Meal places">
      <SettingsNav />
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted">
          Packed lunches at a place only use what’s there and never include its banned allergens.
        </p>
        {canManage ? null : (
          <Alert>Only owners and adults in the household can change meal places.</Alert>
        )}
        <Section
          title="Places"
          accent="teal"
          icon={<HomeIcon className="size-5" />}
          action={
            canManage ? (
              <Button size="sm" block={false} onClick={() => open(NEW_PLACE)}>
                Add a place
              </Button>
            ) : undefined
          }
        >
          {places.isError ? <Alert tone="error">{messageForError(places.error)}</Alert> : null}
          {places.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line" aria-label="Meal places">
              {list.map((p) => (
                <li key={p.id} className="flex flex-wrap items-start justify-between gap-3 py-3">
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <p className="flex flex-wrap items-center gap-1.5 font-medium">
                      <span className="truncate">{p.name}</span>
                      <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-brand">
                        {PLACE_KIND_LABEL[p.kind]}
                      </span>
                    </p>
                    {p.kind === 'home' ? (
                      <p className="text-xs text-muted">Your kitchen — everything is available.</p>
                    ) : (
                      <MiniChips
                        label={`Appliances at ${p.name}`}
                        items={
                          p.appliances.length
                            ? p.appliances.map((a) => APPLIANCE_LABELS[a])
                            : ['No appliances']
                        }
                      />
                    )}
                    <MiniChips
                      label={`Not allowed at ${p.name}`}
                      tone="danger"
                      items={p.bannedAllergens.map((a) => `No ${ALLERGEN_LABELS[a].toLowerCase()}`)}
                    />
                    {p.notes ? <p className="text-xs text-muted">{p.notes}</p> : null}
                  </div>
                  {canManage ? (
                    <div className="flex shrink-0 gap-2">
                      <Button
                        variant="secondary"
                        size="sm"
                        block={false}
                        aria-label={`Edit ${p.name}`}
                        onClick={() => open(draftOf(p))}
                      >
                        Edit
                      </Button>
                      {p.kind === 'home' ? null : (
                        <Button
                          variant="danger"
                          size="sm"
                          block={false}
                          aria-label={`Delete ${p.name}`}
                          onClick={() => {
                            remove.reset();
                            setRemoving(p);
                          }}
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          {places.isSuccess && list.length <= 1 ? (
            <EmptyState
              compact
              icon={<HomeIcon className="size-full text-brand" />}
              title="Lunch at work or school?"
            >
              {canManage
                ? 'Add the place, what it has (a microwave, a fridge) and anything it bans, like a peanut-free school.'
                : 'An owner or adult can add work and school here.'}
            </EmptyState>
          ) : null}
        </Section>
      </div>

      {editing ? (
        <Dialog
          open
          onClose={() => setEditing(null)}
          title={editing.id ? `Edit ${editing.name || 'place'}` : 'Add a place'}
        >
          <form
            noValidate
            onSubmit={onSubmit}
            className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1"
          >
            <Field
              id="place-name"
              label="Name"
              placeholder="e.g. Lincoln Elementary"
              value={editing.name}
              maxLength={80}
              autoFocus
              error={nameError ?? undefined}
              onChange={(e) => setEditing({ ...editing, name: e.target.value })}
            />
            <SelectField
              id="place-kind"
              label="Kind"
              value={editing.kind}
              disabled={editing.kind === 'home'}
              options={placeKindOptions(editing.kind === 'home')}
              onChange={(e) => setEditing({ ...editing, kind: e.target.value as PlaceKind })}
            />
            {editing.kind === 'home' ? null : (
              <ChipToggles
                label="What’s there"
                hint="Leave all off if there’s nothing to heat or chill food."
                keys={APPLIANCES}
                labels={APPLIANCE_LABELS}
                value={editing.appliances}
                onChange={(appliances) => setEditing({ ...editing, appliances })}
              />
            )}
            <ChipToggles
              label="Not allowed here"
              hint="Meals eaten here never include these."
              keys={ALLERGENS}
              labels={ALLERGEN_LABELS}
              tone="danger"
              value={editing.bannedAllergens}
              onChange={(bannedAllergens) => setEditing({ ...editing, bannedAllergens })}
            />
            <div className="flex flex-col gap-1">
              <label htmlFor="place-notes" className="text-sm font-medium text-ink">
                Notes
              </label>
              <textarea
                id="place-notes"
                rows={2}
                maxLength={NOTES_MAX}
                value={editing.notes}
                onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                className="rounded-lg border border-line bg-surface px-3 py-2 text-base text-ink"
              />
            </div>
            {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
            <div className="flex flex-col gap-2 sm:flex-row-reverse">
              <Button type="submit" block={false} disabled={save.isPending}>
                {editing.id ? 'Save place' : 'Add place'}
              </Button>
              <Button type="button" variant="ghost" block={false} onClick={() => setEditing(null)}>
                Cancel
              </Button>
            </div>
          </form>
        </Dialog>
      ) : null}

      <Dialog
        open={removing !== null}
        onClose={() => setRemoving(null)}
        title={removing ? `Delete ${removing.name}?` : 'Delete place'}
      >
        <p className="text-sm text-ink">
          Meal times and planned meals at {removing?.name ?? 'this place'} move back home.
        </p>
        {remove.isError ? <Alert tone="error">{messageForError(remove.error)}</Alert> : null}
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <Button
            variant="danger"
            block={false}
            disabled={remove.isPending}
            onClick={() => removing && remove.mutate(removing.id)}
          >
            Delete place
          </Button>
          <Button variant="ghost" block={false} onClick={() => setRemoving(null)}>
            Cancel
          </Button>
        </div>
      </Dialog>
    </AppShell>
  );
}
